import { ToastMessage, Button } from '@cokitchen/cokitchen-components';
import React, { useEffect, useRef, useState } from 'react';
import { apiService } from 'services/base.service';
import { BrandOption, MealCategoryType } from 'utils/types';
import OrganizeItem from 'components/base/organize-item';
import { toast } from 'react-toastify';
import SelectBrand from 'components/select-brand';
import Category from './category';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';

const MealCategoryListPage = () => {
  const pageHasLoaded = useRef(false);
  const [mealCategories, setMealCategories] = useState([] as MealCategoryType[]);
  const [selectedBrand, setSelectedBrand] = useState<BrandOption | undefined>({
    text: '',
    value: ''
  });
  const [loading, setLoading] = useState(false);
  const [prevMealCategories, setPrevMealCategories] = useState([] as MealCategoryType[]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [dragAndDropActive, setDragAndDropActive] = useState(false);

  const fetchCategoryData = async (reOrder?: boolean) => {
    if (!selectedBrand || !selectedBrand?.value) return;

    try {
      if (!reOrder) setLoading(true);
      const res: any = await apiService.get(
        `marketing/brand/meal-categories/${selectedBrand.value}`
      );
      setMealCategories(res?.data.data);
    } catch {
      setMealCategories([] as any);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pageHasLoaded.current) fetchCategoryData();
    pageHasLoaded.current = true;
  }, [selectedBrand]);
  const onDragEnd = (event: { active: any; over: any }) => {
    const { active, over } = event;
    if (active?.id === over?.id) {
      return;
    }
    setMealCategories((categories) => {
      const oldIndex = categories.findIndex((category) => category.id === active.id);
      const newIndex = categories.findIndex((category) => category.id === over.id);
      return arrayMove(categories, oldIndex, newIndex);
    });
    setStep((prev) => prev + 1);
  };
  const updatePosition = async (items: MealCategoryType[]) => {
    setUpdateLoading(true);
    const meal_category_ids = items.map((item) => item?.id);
    const payload = {
      meal_categories: meal_category_ids,
      brand_id: items[0]?.brand_id,
      is_addon: false
    };
    try {
      const res = await apiService.put(`marketing/meal-category/position`, { data: payload });
      if (res) {
        setStep(0);
        setDragAndDropActive(false);
      }
    } catch (error: any) {
      setMealCategories(prevMealCategories);
      toast(<ToastMessage message={error.response.data.message} type='error' />);
    } finally {
      setUpdateLoading(false);
    }
  };
  const cancelUpdate = () => {
    setMealCategories(prevMealCategories);
    setStep(0);
    setDragAndDropActive(false);
  };

  useEffect(() => {
    if (step === 0 && mealCategories)
      setPrevMealCategories(JSON.parse(JSON.stringify(mealCategories)));
  }, [step, mealCategories]);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );
  return (
    <div className='w-full'>
      <div className='flex justify-between items-center gap-3 flex-wrap w-full mb-10'>
        <div className='max-w-[300px] w-full'>
          <SelectBrand
            selectFirstItem
            brands={selectedBrand ? [selectedBrand] : []}
            setBrands={([b]) => setSelectedBrand(b)}
          />
        </div>

        {!dragAndDropActive ? (
          <OrganizeItem text='meal categories' onClick={() => setDragAndDropActive(true)} />
        ) : (
          <div className='gap-3 flex items-center justify-between'>
            <Button
              small
              className='flex justify-end h-10 w-[100px] ml-auto'
              variant='outline'
              color='primary'
              disabled={updateLoading}
              onClick={cancelUpdate}>
              Cancel
            </Button>

            <Button
              small
              className='flex justify-end h-10 w-[100px] ml-auto'
              variant='block'
              color='primary'
              loading={updateLoading}
              disabled={
                updateLoading ||
                JSON.stringify(mealCategories) === JSON.stringify(prevMealCategories)
              }
              onClick={() => updatePosition(mealCategories ?? [])}>
              Save
            </Button>
          </div>
        )}
      </div>

      <div>
        {loading ? (
          <p className='flex justify-center items-center mt-48'>Loading</p>
        ) : mealCategories?.length === 0 ? (
          <p className='flex justify-center items-center mt-48'>
            Select a brand in order to view meal categories
          </p>
        ) : (
          <div className='grid gap-4 grid-cols-[repeat(auto-fill,_minmax(280px,_1fr))]'>
            {dragAndDropActive ? (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
                sensors={sensors}>
                <SortableContext items={mealCategories}>
                  {mealCategories.map((mealCategory) => (
                    <Category key={mealCategory.id} category={mealCategory} />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              mealCategories.map((mealCategory) => (
                <Category key={mealCategory.id} category={mealCategory} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MealCategoryListPage;
